import { Link, useNavigate, useParams } from 'react-router-dom';
import { sdk } from '@/services/sdk';
import { useFetch } from '@/hooks/useFetch';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { ConfirmDelete, DetailRow, PageHeader, Panel, StatusBadge } from '@/components/kit';
import { ConfirmAction } from '@/components/confirm';
import { ListBody } from '@/components/inputs';
import { BackLink, recordGate } from '@/components/record';
import CopyButton from '@/components/admin/CopyButton';
import DataTable, { RowLink } from '@/components/admin/DataTable';
import {
  DISCOUNT_STATE_STYLES,
  KIND_LABELS,
  PLAN_SCOPE_LABELS,
  discountLabel,
  discountState,
  usesLabel,
} from '@/components/admin/DiscountForm';
import { ORDER_STATUS_STYLES } from '@/shared/constants';
import { DISCOUNT_FIELDS } from '@/shared/fields';
import { committed, idOf, logActivity, rows } from '@/shared/data';
import { money } from '@/shared/pricing';
import { formatLocalDate, formatRelativeTime, DATE_FORMATS } from '@/utils/date';

const { eq } = window.ApperSDK.CoreSDK;

export const route = { path: '/admin/discounts/:id', layout: 'owner', access: "['Admin_c','Staff_c'].includes(profile)" };

export default function AdminDiscountDetail() {
  const { id } = useParams();
  const navigate = useNavigate();

  const { data: code, loading, error, run } = useFetch(
    () => sdk.table('discount_codes_c').select(DISCOUNT_FIELDS).get(id).then((r) => r.data ?? null),
    [id],
  );

  const orders = useFetch(
    () =>
      code?.Name
        ? sdk
            .table('orders_c')
            .select(['Name', 'full_name_c', 'email_c', 'discount_c', 'status_c', 'CreatedOn'])
            .where(eq('discount_code_c', code.Name))
            .orderByDesc('CreatedOn')
            .limit(20)
            .fetch()
            .then(rows)
        : Promise.resolve([]),
    [code?.Name],
  );

  const back = <BackLink to="/admin/discounts" label="Back to Discount codes" />;
  const gate = recordGate({ back, loading, record: code, error, run, what: 'Discount code' });
  if (gate) return gate;

  const state = discountState(code);
  const customerId = idOf(code.customer_c);
  const orderList = orders.data ?? [];

  async function toggleActive() {
    const next = !code.active_c;
    const res = await sdk.table('discount_codes_c').update({ Id: code.Id, active_c: next });
    if (!committed(res, next ? 'Code switched on' : 'Code switched off')) return false;
    logActivity('Code', `${next ? 'Switched on' : 'Switched off'} ${code.Name}`, code.Name);
    run();
    return true;
  }

  async function remove() {
    const res = await sdk.table('discount_codes_c').remove(code.Id);
    if (!committed(res, 'Code deleted')) return false;
    logActivity('Code', `Deleted ${code.Name}`, code.Name);
    navigate('/admin/discounts');
    return true;
  }

  const orderColumns = [
    {
      label: 'Order',
      render: (o) => (
        <RowLink to={`/admin/orders/${o.Id}`} mono sub={o.full_name_c || o.email_c}>
          {o.Name}
        </RowLink>
      ),
    },
    { label: 'Saved', className: 'tabular-nums', render: (o) => money(o.discount_c) },
    { label: 'Status', render: (o) => <StatusBadge value={o.status_c} styles={ORDER_STATUS_STYLES} /> },
    {
      label: 'Placed',
      className: 'hidden sm:table-cell text-right text-muted-foreground',
      render: (o) => formatRelativeTime(o.CreatedOn),
    },
  ];

  return (
    <div className="mx-auto max-w-4xl">
      {back}
      <PageHeader
        eyebrow="Discount code"
        title={<span className="font-mono">{code.Name}</span>}
        description={discountLabel(code)}
        actions={
          <>
            <StatusBadge value={state} styles={DISCOUNT_STATE_STYLES} />
            <CopyButton text={code.Name} label="Copy code" />
            <Button variant="outline" size="sm" asChild>
              <Link to={`/admin/discounts/${code.Id}/edit`}>
                <ApperIcon name="Pencil" size={16} />
                Edit
              </Link>
            </Button>
            <ConfirmDelete
              title="Delete this code?"
              description="Orders that already used it keep their discount. New checkouts can no longer use it."
              onConfirm={remove}
            />
          </>
        }
      />

      <div className="mb-4 flex flex-wrap gap-2">
        <ConfirmAction
          label={code.active_c ? 'Switch off' : 'Switch on'}
          icon={code.active_c ? 'PowerOff' : 'Power'}
          title={code.active_c ? 'Switch this code off?' : 'Switch this code on?'}
          description={
            code.active_c
              ? 'Checkout stops accepting it straight away. You can switch it back on later.'
              : 'Checkout accepts it again, within its expiry and use limits.'
          }
          busyLabel="Saving…"
          onConfirm={toggleActive}
        />
      </div>

      <div className="grid gap-4 lg:grid-cols-2">
        <Panel title="Details" icon="Info">
          <dl className="divide-y divide-border">
            <DetailRow label="Code">
              <span className="font-mono">{code.Name}</span>
            </DetailRow>
            <DetailRow label="Kind">{KIND_LABELS[code.kind_c] ?? code.kind_c}</DetailRow>
            <DetailRow label="Value">{code.kind_c === 'Fixed' ? money(code.value_c) : `${Number(code.value_c || 0)}%`}</DetailRow>
            <DetailRow label="Applies to">{PLAN_SCOPE_LABELS[code.plan_c] ?? code.plan_c}</DetailRow>
            <DetailRow label="Switched on">{code.active_c ? 'Yes' : 'No'}</DetailRow>
            <DetailRow label="Created">{formatLocalDate(code.CreatedOn, DATE_FORMATS.LONG)}</DetailRow>
          </dl>
        </Panel>
        <Panel title="Limits and referral" icon="Gauge">
          <dl className="divide-y divide-border">
            <DetailRow label="Uses">{usesLabel(code)}</DetailRow>
            <DetailRow label="Max uses">{code.max_uses_c ? Number(code.max_uses_c) : 'No limit'}</DetailRow>
            <DetailRow label="Expires">
              {code.expires_at_c ? formatLocalDate(code.expires_at_c, DATE_FORMATS.WITH_TIME) : 'Never'}
            </DetailRow>
            <DetailRow label="Referrer">
              {customerId ? (
                <Link to={`/admin/customers/${customerId}`} className="text-primary hover:underline">
                  {code.customer_c?.Name ?? 'View customer'}
                </Link>
              ) : null}
            </DetailRow>
            <DetailRow label="Credit used">{money(code.credit_used_c)}</DetailRow>
          </dl>
        </Panel>
      </div>

      <section className="mt-6">
        <h2 className="mb-3 flex items-center gap-2 text-sm font-semibold">
          <ApperIcon name="ShoppingBag" size={16} className="text-primary" />
          Orders that used it
        </h2>
        <ListBody
          loading={orders.loading}
          data={orders.data}
          error={orders.error}
          run={orders.run}
          rows={3}
          isEmpty={!orderList.length}
          empty={{ icon: 'ShoppingBag', title: 'Not used yet', description: 'Orders placed with this code show up here.' }}
        >
          <DataTable columns={orderColumns} rows={orderList} />
        </ListBody>
      </section>
    </div>
  );
}
